/**
 * Appeal Helper Functions
 * 
 * Utilities for the appeal generation workflow including
 * specificity detection and optional detail handling.
 */

import type { OptionalAppealDetails } from "../components/Appeals/StrengthenAppealModal";

/**
 * Case data interface for specificity checking
 * Matches the structure from Supabase cases table
 */
interface CaseData {
  diagnoses?: string | null;
  request_type?: string | null;
  request_details?: string | null;
  denial_reason?: string | null;
  denial_details?: string | null;
  tried_already?: string | null;
  clinical_history?: string | null;
  age_range?: string | null;
  sex?: string | null;
  [key: string]: unknown;
}

/**
 * Checks if a case has low specificity and would benefit from
 * the "Strengthen Appeal" modal.
 * 
 * Returns TRUE only if ALL of these are missing:
 * 1. Symptom/diagnosis/problem
 * 2. Duration/severity/course
 * 3. Failed treatment or relevant context
 * 
 * If ANY one exists → returns false (don't prompt)
 */
export function needsAppealDetails(caseData: CaseData | null): boolean {
  if (!caseData) return true;

  // Check for symptom/diagnosis/problem
  const hasSymptomOrDiagnosis = Boolean(
    caseData.diagnoses?.trim() ||
    caseData.request_details?.trim()
  );

  // Check for duration/severity/course indicators
  // Look for time-related words or severity indicators in any text field
  const allText = [
    caseData.diagnoses,
    caseData.request_details,
    caseData.denial_details,
    caseData.clinical_history,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  const durationPatterns = [
    /\d+\s*(day|week|month|year)/i,
    /chronic/i,
    /acute/i,
    /ongoing/i,
    /persistent/i,
    /recurrent/i,
    /worsening/i,
    /progressive/i,
    /severe/i,
    /mild/i,
    /moderate/i,
  ];
  
  const hasDurationOrSeverity = durationPatterns.some((pattern) =>
    pattern.test(allText)
  );

  // Check for failed treatment or relevant context
  const hasTriedOrContext = Boolean(
    caseData.tried_already?.trim() ||
    caseData.clinical_history?.trim()
  );

  // Also check for treatment-related words in other fields
  const treatmentPatterns = [
    /failed/i,
    /tried/i,
    /no improvement/i,
    /contraindicated/i,
    /intolerant/i,
    /conservative/i,
    /first-line/i,
    /nsaid/i,
    /physical therapy/i,
  ];
  
  const hasTreatmentMentioned = treatmentPatterns.some((pattern) =>
    pattern.test(allText)
  );

  // Return TRUE only if ALL are missing
  // If any single category has data, don't prompt
  const hasAnySpecificity =
    hasSymptomOrDiagnosis ||
    hasDurationOrSeverity ||
    hasTriedOrContext ||
    hasTreatmentMentioned;

  return !hasAnySpecificity;
}

/**
 * Merges optional appeal details into additional context string
 * for the appeal generator.
 * 
 * Returns a formatted string that can be appended to the
 * additionalContext parameter sent to the edge function.
 */
export function formatAppealDetailsForGenerator(
  details: OptionalAppealDetails
): string {
  const parts: string[] = [];

  if (details.clinicalIssue?.trim()) {
    parts.push(`Clinical issue: ${details.clinicalIssue.trim()}`);
  }

  if (details.durationSeverity?.trim()) {
    parts.push(`Duration/severity: ${details.durationSeverity.trim()}`);
  }

  if (details.triedOrContext?.trim()) {
    parts.push(`Context: ${details.triedOrContext.trim()}`);
  }

  return parts.join("\n");
}

/**
 * Checks if the user has dismissed the strengthen modal permanently
 * (stored in localStorage)
 */
export function hasUserDismissedStrengthenModal(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem("peerprep_dismiss_strengthen_modal") === "true";
}

/**
 * Sets the user preference to not show the strengthen modal
 */
export function setDismissStrengthenModal(dismiss: boolean): void {
  if (typeof window === "undefined") return;
  if (dismiss) {
    localStorage.setItem("peerprep_dismiss_strengthen_modal", "true");
  } else {
    localStorage.removeItem("peerprep_dismiss_strengthen_modal");
  }
}